import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { AuthService } from '../auth/auth.service';
import { CashCount, CashCurrency, CurrencyProfile, resolveCurrencyProfile } from 'src/app/shared/cash-count/cash-count';
import { isCourtesyOrder } from 'src/app/shared/courtesy/courtesy.model';

/**
 * Arqueo de un método de pago: lo que dice el sistema contra lo que se contó.
 * Solo el efectivo se cuenta físicamente; tarjeta y POS llegan conciliados
 * desde el procesador y se guardan como referencia.
 */
export interface ClosingCount {
    /** Lo que registró el sistema para ese método. */
    system: number;
    /** Lo que la persona contó. null = no se arqueó (se da por bueno). */
    counted: number | null;
}

/**
 * 'pre'      → cuadre provisional: la caja sigue abierta y puede repetirlo.
 * 'final'    → cierre definitivo de esa caja para el evento.
 * 'canceled' → pre-cierre anulado: se conserva como historial pero la caja
 *              vuelve a estar sin cerrar (getClosing lo devuelve como null).
 */
export type ClosingStatus = 'pre' | 'final' | 'canceled';

export interface EventClosing {
    status: ClosingStatus;
    event_key: string;
    event_name: string;
    countryCode: string;

    /**
     * Caja que cierra = zona del evento (order.event_category_key), p. ej.
     * "Barra 1". No es el storeKey: todos los cajeros de un evento comparten
     * la misma tienda, y agrupar por ella los habría metido en una sola caja.
     */
    box_key: string;
    box_name: string;

    /** Totales del sistema al momento de cerrar. */
    total_orders: number;
    total_sales: number;
    total_cost_admin: number;

    counts: {
        cash: ClosingCount;
        card: ClosingCount;
        pos: ClosingCount;
    };

    /** Moneda del arqueo según el país (SV → USD, HN → HNL…): código ISO, símbolo y nombre. */
    currency?: CashCurrency | null;

    /**
     * Conteo físico por denominación: cuántos billetes y monedas de cada
     * valor, con subtotales. Su total es counts.cash.counted. null si se
     * cerró sin arqueo o la moneda no tiene catálogo de denominaciones.
     */
    cash_count?: CashCount | null;

    /** Fondo inicial de caja y gastos pagados en efectivo durante el evento. */
    opening_float: number;
    cash_expenses: number;

    /** counted − esperado. Positivo = sobrante, negativo = faltante. */
    difference: number;

    notes: string;

    /** Del último pre-cierre. Se conserva aunque después se cierre. */
    pre_closed_by?: string;
    pre_closed_at?: any;
    /** Totales al momento del pre-cierre, para detectar ventas posteriores. */
    pre_total_sales?: number;
    pre_total_cash?: number;

    /** Solo cuando status === 'final'. */
    closed_by?: string;
    closed_at?: any;

    /** Solo cuando status === 'canceled'. */
    canceled_by?: string;
    canceled_at?: any;
}

/** Totales de una caja (zona) dentro de un evento, calculados desde sus órdenes. */
export interface BoxTotals {
    box_key: string;
    box_name: string;
    total_orders: number;
    total_sales: number;
    /** Cargo de boletería cobrado en esa caja (0 en eventos sin boletería). */
    total_cost_admin: number;
    total_cash: number;
    total_card: number;
    total_pos: number;
    /** Regalías (órdenes con total 0) que salieron de esa caja; van dentro de total_orders. */
    total_courtesy: number;
    /** Cierre de esa caja, si ya lo hizo. */
    closing?: EventClosing | null;
    /** Desglose por cajero: la caja suma a todos sus usuarios. */
    cashiers?: CashierTotals[];
}

/** Lo que vendió un cajero (cuenta que creó las órdenes) dentro de una caja. */
export interface CashierTotals {
    account_key: string;
    name: string;
    email: string;
    total_orders: number;
    total_sales: number;
    total_cash: number;
    total_card: number;
    total_pos: number;
}

export interface ReportValidation {
    event_key: string;
    countryCode: string;
    validated_by: string;
    validated_at: any;
    notes: string;
}

@Injectable({
    providedIn: 'root'
})
export class EventClosingService {

    /** Documento de configuración del país (SV vive en 'general'). */
    private get configPath(): string {
        return this.auth.countryCode === 'SV' ? 'general' : this.auth.countryCode;
    }

    constructor(
        private api: ApiService,
        private auth: AuthService,
    ) { }

    // ═══════════════════════════════════════════════════════════════════════
    //  CONFIGURACIÓN
    // ═══════════════════════════════════════════════════════════════════════

    /** Roles que pueden exigir el cierre de caja para un evento concreto. */
    static readonly CAN_TOGGLE_EVENT_CLOSING = ['0', '1', '2', '11', '18'];

    canToggleEventClosing(): boolean {
        return EventClosingService.CAN_TOGGLE_EVENT_CLOSING.indexOf(String(this.auth.role)) >= 0;
    }

    /**
     * ¿Este evento exige cierre con arqueo?
     * Manda el evento: si events/{key}.closing_required está definido, se
     * usa. Si no, cae a la bandera del país en configurations. Así un
     * administrador puede encenderlo para todos, y el responsable de un
     * evento (rol 11) puede decidirlo para el suyo sin tocar el país.
     * Sin ninguna de las dos se asume que no: exigir cierre debe ser una
     * decisión explícita, no el efecto de que falte un campo.
     */
    async isClosingRequired(eventKey?: string): Promise<boolean> {
        if (eventKey) {
            try {
                const ev: any = await this.api.getDocument('events', eventKey);
                if (ev && typeof ev.closing_required === 'boolean') {
                    return ev.closing_required;
                }
            } catch (e) {
                console.error('No se pudo leer closing_required del evento:', e);
            }
        }
        return this.isGlobalClosingRequired();
    }

    /** Bandera del país, sin mirar el evento. */
    async isGlobalClosingRequired(): Promise<boolean> {
        try {
            const config: any = await this.api.getDocument('configurations', this.configPath);
            return !!(config && config.event_closing_active === true);
        } catch (e) {
            console.error('No se pudo leer event_closing_active:', e);
            return false;
        }
    }

    /** Exige (o deja de exigir) el cierre para un evento en particular. */
    async setEventClosingRequired(eventKey: string, active: boolean): Promise<any> {
        return this.api.updateDocument('events', eventKey, {
            closing_required: !!active
        });
    }

    /** Activa o desactiva el cierre obligatorio para todo el país. */
    async setClosingRequired(active: boolean): Promise<any> {
        return this.api.updateDocument('configurations', this.configPath, {
            event_closing_active: !!active
        });
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  MONEDA
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Moneda con la que arquea esta sesión: la del país, con sus billetes y
     * monedas. null si el país no está en el catálogo y la moneda de la
     * tienda tampoco se reconoce: entonces el cierre pide el total a mano.
     */
    currencyProfile(): CurrencyProfile | null {
        return resolveCurrencyProfile(this.auth.countryCode, this.auth.currency);
    }

    /** Moneda tal como se guarda en el cierre; sin catálogo, al menos el símbolo de la tienda. */
    currencyInfo(): CashCurrency {
        const profile = this.currencyProfile();
        if (profile) return { code: profile.code, symbol: profile.symbol, name: profile.name };
        const raw = String(this.auth.currency || '');
        return { code: raw.length === 3 ? raw.toUpperCase() : '', symbol: raw, name: '' };
    }

    /** Símbolo para textos armados a mano (los avisos); las plantillas usan el pipe currency. */
    currencySymbol(): string {
        return this.currencyInfo().symbol;
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  CÁLCULO DEL ARQUEO
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Efectivo que debería haber en caja al cerrar:
     * fondo inicial + ventas en efectivo − gastos pagados en efectivo.
     */
    expectedCash(systemCash: number, openingFloat: number, cashExpenses: number): number {
        return this.round(Number(systemCash || 0) + Number(openingFloat || 0) - Number(cashExpenses || 0));
    }

    /**
     * Diferencia del arqueo: contado − esperado.
     * Positivo = sobrante, negativo = faltante, 0 = cuadra.
     * Devuelve null si no se contó nada, para no confundir "no arqueado"
     * con "arqueado y cuadra exacto".
     */
    cashDifference(counted: number | null, expected: number): number | null {
        if (counted === null || counted === undefined || counted === '' as any) return null;
        return this.round(Number(counted) - Number(expected || 0));
    }

    /** Un descuadre menor a un centavo es ruido de redondeo, no un faltante. */
    isBalanced(difference: number | null): boolean {
        if (difference === null) return true;
        return Math.abs(difference) < 0.01;
    }

    private round(n: number): number {
        return Math.round(Number(n || 0) * 100) / 100;
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  PERSISTENCIA
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Un cierre por caja y por evento: el id combina país, evento y caja.
     * Volver a guardar sobrescribe el cierre de esa caja, no duplica.
     */
    private closingId(eventKey: string, boxKey: string): string {
        return `${this.auth.countryCode}_${eventKey}_${boxKey}`;
    }

    /** Id de la validación (auditoría): es del evento, no de una caja. */
    private validationId(eventKey: string): string {
        return `${this.auth.countryCode}_${eventKey}`;
    }

    async getClosing(eventKey: string, boxKey: string): Promise<EventClosing | null> {
        if (!eventKey || !boxKey) return null;
        try {
            const doc: any = await this.api.getDocument('event_closings', this.closingId(eventKey, boxKey));
            // Un pre-cierre anulado no cuenta: la caja está como si nunca hubiera contado.
            if (!doc || doc.status === 'canceled') return null;
            return doc;
        } catch (e) {
            return null;
        }
    }

    /**
     * Todos los cierres de un evento, para la auditoría del rol 11.
     * Solo filtros de igualdad para no exigir índices compuestos nuevos.
     */
    async getClosingsForEvent(eventKey: string): Promise<EventClosing[]> {
        if (!eventKey) return [];
        try {
            const snap = await this.api.getRef('event_closings').ref
                .where('countryCode', '==', this.auth.countryCode)
                .where('event_key', '==', eventKey)
                .get();

            const list: EventClosing[] = [];
            snap.forEach(doc => {
                const closing = doc.data() as EventClosing;
                if (closing && closing.status !== 'canceled') list.push(closing);
            });
            return list;
        } catch (e) {
            console.error('No se pudieron leer los cierres del evento:', e);
            return [];
        }
    }

    /**
     * Guarda el pre-cierre o el cierre definitivo de una caja.
     * El pre-cierre NO escribe closed_at: mientras no exista esa marca, la
     * caja se considera abierta aunque ya se haya contado el efectivo.
     */
    async saveClosing(closing: EventClosing): Promise<any> {
        const now = new Date();
        const isFinal = closing.status === 'final';

        const payload: any = {
            ...closing,
            countryCode: this.auth.countryCode,
            pre_closed_by: this.auth.email,
            pre_closed_at: closing.pre_closed_at || now,
        };

        if (isFinal) {
            payload.closed_by = this.auth.email;
            payload.closed_at = now;
        } else {
            // Rehacer el pre-cierre refresca su marca de tiempo.
            payload.pre_closed_at = now;
            payload.closed_by = null;
            payload.closed_at = null;
        }

        // En cola: sin conexión el cierre queda guardado en el dispositivo y
        // se envía solo al recuperar la señal; la pantalla no se queda esperando.
        this.api.setDocumentQueued('event_closings', this.closingId(closing.event_key, closing.box_key), payload);
    }

    /**
     * Anula el pre-cierre de una caja: la caja vuelve a estar sin cerrar y
     * el próximo pre-cierre arranca de cero. Se conserva el documento con
     * el conteo anulado, quién y cuándo, como historial. Solo aplica al
     * pre-cierre: el cierre en firme no se deshace desde la caja.
     */
    async cancelClosing(eventKey: string, boxKey: string): Promise<void> {
        this.api.setDocumentQueued('event_closings', this.closingId(eventKey, boxKey), {
            status: 'canceled',
            canceled_by: this.auth.email || '',
            canceled_at: new Date(),
        }, { merge: true });
    }

    /** Una caja está cerrada solo si su cierre es definitivo. */
    isFinal(closing: EventClosing | null): boolean {
        return !!(closing && closing.status === 'final');
    }

    /** Hay pre-cierre pero todavía no se cierra. */
    isPreOnly(closing: EventClosing | null): boolean {
        return !!(closing && closing.status === 'pre');
    }

    /**
     * Ventas registradas después del pre-cierre. Si es mayor que cero, la
     * caja siguió facturando desde que contó y el conteo quedó viejo.
     */
    salesAfterPreClose(closing: EventClosing | null, currentSales: number): number {
        if (!closing || closing.pre_total_sales === undefined || closing.pre_total_sales === null) return 0;
        const diff = Number(currentSales || 0) - Number(closing.pre_total_sales || 0);
        return diff > 0.009 ? this.round(diff) : 0;
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  ÓRDENES DEL EVENTO
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Todas las órdenes del evento, de todas las zonas. La auditoría no
     * puede apoyarse en lo que tenga cargado la pantalla, porque monitor-v3
     * filtra por la zona seleccionada y mostraría una sola caja.
     * Dos filtros de igualdad: no requiere índice compuesto.
     */
    async getEventOrders(eventKey: string): Promise<any[]> {
        if (!eventKey) return [];
        try {
            const snap = await this.api.getRef('orders').ref
                .where('countryCode', '==', this.auth.countryCode)
                .where('event_key', '==', eventKey)
                .get();
            const list: any[] = [];
            snap.forEach(doc => {
                const order = doc.data();
                if (order.deleted === true) return;
                order.$key = doc.id;
                list.push(order);
            });
            return list;
        } catch (e) {
            console.error('No se pudieron leer las órdenes del evento:', e);
            return [];
        }
    }

    /**
     * Todas las órdenes de una caja (zona) del evento, y si se indica la
     * cuenta, solo las que vendió ese usuario/estación. El cierre no puede
     * apoyarse en lo que tenga cargado la pantalla: el monitor pagina de a
     * diez y solo trae los estados marcados en "Obtener datos".
     * Solo filtros de igualdad: no requiere índice compuesto.
     */
    async getBoxOrders(eventKey: string, boxKey: string, accountKey: string = ''): Promise<any[]> {
        if (!eventKey || !boxKey) return [];
        const snap = await this.api.getRef('orders').ref
            .where('countryCode', '==', this.auth.countryCode)
            .where('event_key', '==', eventKey)
            .where('event_category_key', '==', boxKey)
            .get();

        const list: any[] = [];
        snap.forEach(doc => {
            const order = doc.data();
            if (order.deleted === true) return;
            order.$key = doc.id;
            if (accountKey && this.orderAccountKey(order) !== accountKey) return;
            list.push(order);
        });
        return list;
    }

    /** La cuenta viene como llave o, ya resuelta por el monitor, como objeto. */
    private orderAccountKey(order: any): string {
        const account = order ? order.account : null;
        if (account && typeof account === 'object') {
            return account.$key || account.id || account.token || '';
        }
        return account || '';
    }

    /** Totales de UNA caja a partir de sus órdenes (vacíos si no vendió). */
    boxTotalsFromOrders(orders: any[], boxKey: string, boxName: string = ''): BoxTotals {
        const found = this.groupOrdersByBox(orders).find(box => box.box_key === boxKey);
        if (found) {
            if (!found.box_name) found.box_name = boxName;
            return found;
        }
        return {
            box_key: boxKey,
            box_name: boxName,
            total_orders: 0,
            total_sales: 0,
            total_cost_admin: 0,
            total_cash: 0,
            total_card: 0,
            total_pos: 0,
            total_courtesy: 0,
        };
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  AGRUPACIÓN POR CAJA (ZONA)
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Parte las órdenes del evento por caja, es decir por zona
     * (order.event_category_key). Es lo que ve el rol 11 en la auditoría y
     * lo que usa el rol 8 para cerrar la suya.
     * Las canceladas no suman: no hay dinero que arquear detrás de ellas.
     */
    groupOrdersByBox(orders: any[]): BoxTotals[] {
        const map: { [key: string]: BoxTotals } = {};

        (orders || []).forEach(order => {
            if (!order || order.status === 'canceled') return;

            const boxKey = order.event_category_key || 'sin_caja';
            if (!map[boxKey]) {
                map[boxKey] = {
                    box_key: boxKey,
                    box_name: '',
                    total_orders: 0,
                    total_sales: 0,
                    total_cost_admin: 0,
                    total_cash: 0,
                    total_card: 0,
                    total_pos: 0,
                    total_courtesy: 0,
                };
            }

            const amount = Number(order.total || 0);
            const bucket = map[boxKey];
            bucket.total_orders++;
            if (isCourtesyOrder(order)) bucket.total_courtesy++;
            bucket.total_sales = this.round(bucket.total_sales + amount);
            bucket.total_cost_admin = this.round(bucket.total_cost_admin + Number(order.cost_admin || 0));

            const type = (order.payment && order.payment.type) ? order.payment.type : '';
            if (type === 'cash') bucket.total_cash = this.round(bucket.total_cash + amount);
            else if (type === 'card') bucket.total_card = this.round(bucket.total_card + amount);
            else if (type === 'pos') bucket.total_pos = this.round(bucket.total_pos + amount);
        });

        return Object.keys(map)
            .map(k => map[k])
            .sort((a, b) => b.total_sales - a.total_sales);
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  CAJEROS DE UNA CAJA
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Parte las órdenes de una caja por cajero (la cuenta que creó cada
     * orden). La suma de los cajeros es el total de la caja: así el cierre
     * de una barra con varios usuarios incluye a todos.
     */
    groupOrdersByCashier(orders: any[], boxKey: string = ''): CashierTotals[] {
        const map: { [key: string]: CashierTotals } = {};
        (orders || []).forEach(order => {
            if (!order || order.status === 'canceled') return;
            if (boxKey && (order.event_category_key || 'sin_caja') !== boxKey) return;
            const key = this.orderAccountKey(order) || 'sin_cuenta';
            if (!map[key]) {
                map[key] = { account_key: key, name: '', email: '', total_orders: 0, total_sales: 0, total_cash: 0, total_card: 0, total_pos: 0 };
            }
            const amount = Number(order.total || 0);
            const bucket = map[key];
            bucket.total_orders++;
            bucket.total_sales = this.round(bucket.total_sales + amount);
            const type = (order.payment && order.payment.type) ? order.payment.type : '';
            if (type === 'cash') bucket.total_cash = this.round(bucket.total_cash + amount);
            else if (type === 'card') bucket.total_card = this.round(bucket.total_card + amount);
            else if (type === 'pos') bucket.total_pos = this.round(bucket.total_pos + amount);
        });
        return Object.keys(map).map(k => map[k]).sort((a, b) => b.total_sales - a.total_sales);
    }

    private accountNames: { [key: string]: { name: string; email: string } } = {};

    /** Nombre y correo de cada cajero (accounts/{uid}; si no, users-stores). Cachea por sesión. */
    async resolveCashierNames(cashiers: CashierTotals[]): Promise<CashierTotals[]> {
        await Promise.all((cashiers || []).map(async cashier => {
            const key = cashier.account_key;
            if (!key || key === 'sin_cuenta') {
                cashier.name = 'Sin cuenta';
                return;
            }
            if (!this.accountNames[key]) {
                let info: any = null;
                try {
                    info = await this.api.getDocument('accounts', key);
                } catch (e) {
                    try { info = await this.api.getDocument('users-stores', key); } catch (err) { info = null; }
                }
                const name = info ? `${info.name || ''} ${info.lastname || ''}`.trim() : '';
                this.accountNames[key] = { name: name || (info && info.email) || key, email: info && info.email ? String(info.email) : '' };
            }
            cashier.name = this.accountNames[key].name;
            cashier.email = this.accountNames[key].email;
        }));
        return cashiers;
    }

    /**
     * Cierre en firme de todas las cajas que aún no cerraron, con los totales
     * del sistema (todos los cajeros de cada caja) y sin arqueo físico. Lo
     * usa el responsable del evento (rol 11) para cerrar el evento completo.
     * Devuelve cuántas cajas se cerraron.
     */
    async closeAllBoxes(eventKey: string, eventName: string, boxes: BoxTotals[], notes: string = ''): Promise<number> {
        let count = 0;
        for (const box of boxes || []) {
            if (!box || !box.box_key || box.box_key === 'sin_caja' || this.isFinal(box.closing)) continue;
            await this.saveClosing({
                status: 'final',
                event_key: eventKey,
                event_name: eventName || '',
                countryCode: this.auth.countryCode,
                box_key: box.box_key,
                box_name: box.box_name || box.box_key,
                total_orders: Number(box.total_orders || 0),
                total_sales: Number(box.total_sales || 0),
                total_cost_admin: Number(box.total_cost_admin || 0),
                counts: {
                    cash: { system: Number(box.total_cash || 0), counted: null },
                    card: { system: Number(box.total_card || 0), counted: null },
                    pos: { system: Number(box.total_pos || 0), counted: null },
                },
                currency: this.currencyInfo(),
                cash_count: null,
                opening_float: 0,
                cash_expenses: 0,
                difference: null,
                notes: notes || 'Cierre de todas las cajas por el responsable del evento (sin arqueo físico).',
                pre_total_sales: Number(box.total_sales || 0),
                pre_total_cash: Number(box.total_cash || 0),
                pre_closed_at: box.closing ? box.closing.pre_closed_at : null,
            });
            count++;
        }
        return count;
    }

    /** Rellena el nombre de cada caja: las zonas viven en bricks. */
    async resolveBoxNames(boxes: BoxTotals[]): Promise<BoxTotals[]> {
        await Promise.all((boxes || []).map(async box => {
            if (!box.box_key || box.box_key === 'sin_caja') {
                box.box_name = 'Sin zona asignada';
                return;
            }
            try {
                const doc: any = await this.api.getDocument('bricks', box.box_key);
                box.box_name = (doc && doc.name) ? doc.name : box.box_key;
            } catch (e) {
                box.box_name = box.box_key;
            }
        }));
        return boxes;
    }

    /**
     * Cruza los totales calculados con los cierres ya guardados.
     * Deja en cada caja su cierre (o null si todavía no cerró).
     */
    attachClosings(boxes: BoxTotals[], closings: EventClosing[]): BoxTotals[] {
        (boxes || []).forEach(box => {
            box.closing = (closings || []).find(c => c && c.box_key === box.box_key) || null;
        });
        return boxes;
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  VALIDACIÓN DE REPORTES
    // ═══════════════════════════════════════════════════════════════════════

    async getValidation(eventKey: string): Promise<ReportValidation | null> {
        if (!eventKey) return null;
        try {
            const doc: any = await this.api.getDocument('report_validations', this.validationId(eventKey));
            return doc || null;
        } catch (e) {
            return null;
        }
    }

    /** Deja constancia de quién revisó el reporte y cuándo. En cola, como saveClosing. */
    async validateReport(eventKey: string, eventName: string, notes: string = ''): Promise<any> {
        this.api.setDocumentQueued('report_validations', this.validationId(eventKey), {
            event_key: eventKey,
            event_name: eventName || '',
            countryCode: this.auth.countryCode,
            validated_by: this.auth.email,
            validated_at: new Date(),
            notes: notes || '',
        });
    }
}
